import { useState, useEffect } from "react";
import { Checkbox, Form, Input, Radio, Spin, Switch, Table, Upload } from "antd";
import { Link } from "react-router-dom";
import PdfPreviewImg from '../../../static/img/files/pdf.png';
import TextPreviewImg from '../../../static/img/textFileIcon.svg';
import MdPreviewImg from '../../../static/img/mdFileIcon.png';
import CancelImg from '../../../static/img/Search-cancel.svg';
import VisibilitySelector from "./VisibilitySelector";
import { TableWrapper } from "../../styled";
import { UserTableStyleWrapper } from "../style";
import Heading from "../../../components/heading/heading";
import { Button } from "../../../components/buttons/buttons";
import Notification from "../../../utility/notification";
import editIcon from "../../../../src/static/img/edit-icon.svg";
import deleteIcon from "../../../../src/static/img/trash_bin_icon.svg";

const toLabelCase = (v) => (typeof v === "string" ? v.charAt(0).toUpperCase() + v.slice(1) : "");

// ─── Question Card ───

export const QuestionCard = ({ question, listingLabel, onEdit, onDelete, isDeleting, readOnly = false, showToggle = false }) => {
    const visibilityTags = question.visibility?.includes("all") ? ["all"] : (question.visibility || []);

    const desc = question.description || "";
    const lowerDesc = desc.toLowerCase();
    const isFileUrl = desc.startsWith("http");
    const isPdf = lowerDesc.endsWith(".pdf");
    const isTxt = lowerDesc.endsWith(".txt");
    const isMd = lowerDesc.endsWith(".md");
    const hasDocPreview = isPdf || isTxt || isMd;
    const isImage = isFileUrl && !hasDocPreview;

    return (
        <Spin spinning={isDeleting}>
            <div className="ai-kb-resource-card">
                <div className="ai-kb-resource-card-left">
                    <div className="ai-kb-resource-card-content">
                        <div className="ai-kb-tags">
                            {visibilityTags.map((vis) => (
                                <span key={vis} className="ai-kb-tag">{toLabelCase(vis)}</span>
                            ))}
                        </div>
                        <h4 className="project-detail-lable mb-0">{question.title}</h4>
                        {isFileUrl ? (
                            <div className="ai-kb-thumbnail-wrap">
                                <Link
                                    to="#"
                                    className={`image-preview static-preview ai-kb-doc-preview-link ${hasDocPreview ? "ai-kb-file-preview-link" : "ai-kb-image-preview-link"}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        window.open(desc, "_blank", "noopener,noreferrer");
                                    }}
                                >
                                    {hasDocPreview ? (
                                        <img
                                            src={isTxt ? TextPreviewImg : isMd ? MdPreviewImg : PdfPreviewImg}
                                            alt={isTxt ? "Text file" : isMd ? "Markdown file" : "PDF file"}
                                            className="static-img"
                                        />
                                    ) : (
                                        <img src={desc} alt="Preview" className="ai-kb-preview-image" />
                                    )}
                                </Link>
                            </div>
                        ) : (
                            desc && <span className="project-detail-value">{desc}</span>
                        )}
                    </div>
                </div>

                <div className="ai-kb-resource-card-right">
                    {/* {showToggle && (
                        <Switch size="large" className="switch-toggle"  defaultChecked />
                    )} */}
                    <span className="ai-kb-listing-name">{listingLabel}</span>
                    <div className="ai-kb-resource-actions">
                        {!readOnly && (
                            <button type="button" className="ai-kb-icon-btn" onClick={() => onEdit(question)} aria-label="Edit question">
                                <img src={editIcon} alt="edit" />
                            </button>
                        )}
                        {!readOnly && (
                            <button type="button" className="ai-kb-icon-btn is-delete" onClick={() => onDelete(question)} aria-label="Delete question">
                                <img src={deleteIcon} alt="delete" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </Spin>
    );
};

// ─── Question Editor ────

export const QuestionEditor = ({
    draft,
    isSaving,
    isUploading,
    onChange,
    onVisibility,
    onOpenListingPopup,
    onSave,
    onCancel,
    onFilePicked,
    onFileRemove,
    showHostComponent,
    allowFileUpload = false,
}) => {
    const [form] = Form.useForm();
    const [answerMode, setAnswerMode] = useState(() =>
        draft?.description?.startsWith("http") ? "file" : "text"
    );

    const lowerDescription = draft?.description?.toLowerCase() || "";
    const isPdf = lowerDescription.endsWith(".pdf");
    const isTxt = lowerDescription.endsWith(".txt");
    const isMd = lowerDescription.endsWith(".md");
    const hasDocPreview = isPdf || isTxt || isMd;

    useEffect(() => {
        form.setFieldsValue({ title: draft.title, description: draft.description, fileName: draft.fileName });
        // Auto-switch mode when editing an existing item that has a file URL
        if (allowFileUpload) {
            setAnswerMode(draft?.description?.startsWith("http") ? "file" : "text");
        }
    }, [draft.title, draft.description, draft.fileName]);

    // When switching to text mode clear the file fields, and vice versa
    const handleModeChange = (e) => {
        const mode = e.target.value;
        setAnswerMode(mode);
        if (mode === "text") {
            onChange("fileName", "");
            onChange("description", "");
            form.setFieldsValue({ fileName: "", description: "" });
        } else {
            onChange("description", "");
            form.setFieldsValue({ description: "" });
        }
    };

    const handleSave = () => {
        form.validateFields().then(() => onSave()).catch(() => { });
    };

    return (
        <div className="ai-kb-resource-card ai-kb-resource-card-editor">
            <div className="ai-kb-editor-top">
                <VisibilitySelector selected={draft.visibility} onSelect={onVisibility} />
                {showHostComponent && (
                    <button type="button" className="ai-kb-select-listing-btn" onClick={onOpenListingPopup}>
                        Select Listings
                    </button>
                )}
            </div>

            <Form form={form} layout="vertical">
                <div className="ai-kb-editor-fields">
                    <Form.Item
                        name="title"
                        className="listing_field_name mb-0"
                        rules={[{ required: true, whitespace: true, message: "Please enter question." }]}
                    >
                        <Input
                            placeholder="Enter question"
                            value={draft.title}
                            className="ai-kb-question-input"
                            onChange={(e) => onChange("title", e.target.value)}
                        />
                    </Form.Item>

                    {/* Answer type radio — only shown in messages AI tab */}
                    {allowFileUpload && (
                        <Radio.Group
                            value={answerMode}
                            onChange={handleModeChange}
                            className="mb-10"
                        >
                            <Radio value="text">Write Answer</Radio>
                            <Radio value="file">Upload File</Radio>
                        </Radio.Group>
                    )}

                    {/* File upload — Resources-style */}
                    {allowFileUpload && answerMode === "file" ? (
                        <>
                            <Form.Item
                                name="fileName"
                                className="mb-0 ai-kb-question-input"
                                rules={[
                                    {
                                        validator: (_, value) => {
                                            const hasDocument = draft?.description?.startsWith("https");
                                            if (hasDocument || isPdf || isTxt || isMd) return Promise.resolve();
                                            return value ? Promise.resolve() : Promise.reject(new Error("Please select a file."));
                                        },
                                    },
                                ]}
                            >
                                <Spin spinning={!!isUploading}>
                                    <Upload
                                        accept=".pdf,.txt,.md,image/*"
                                        className="ninjadash-upload-basic"
                                        showUploadList={false}
                                        beforeUpload={() => false}
                                        onChange={onFilePicked}
                                        onClick={(e) => { e.currentTarget.value = null; }}
                                    >
                                        <span className="ninjadash-upload-text">Select File</span>
                                        <Link to="#" className="ninjadash-upload-browse">Browse</Link>
                                    </Upload>
                                </Spin>
                            </Form.Item>

                            {draft.description && (
                                <div className="ai-kb-preview-wrap">
                                    <Link
                                        to="#"
                                        className={`image-preview static-preview ai-kb-doc-preview-link ${hasDocPreview ? "ai-kb-file-preview-link" : "ai-kb-image-preview-link"}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            window.open(draft.description, "_blank", "noopener,noreferrer");
                                        }}
                                    >
                                        {hasDocPreview ? (
                                            <img
                                                src={isTxt ? TextPreviewImg : isMd ? MdPreviewImg : PdfPreviewImg}
                                                alt={isTxt ? "Text file" : isMd ? "Markdown file" : "PDF file"}
                                                className="static-img"
                                            />
                                        ) : (
                                            <img src={draft.description} alt="Preview" className="ai-kb-preview-image" />
                                        )}
                                    </Link>
                                    <span onClick={onFileRemove} className="ai-kb-preview-remove">
                                        <img src={CancelImg} alt="remove" />
                                    </span>
                                </div>
                            )}
                        </>
                    ) : (
                        /* Text answer — Others-style textarea */
                        <Form.Item
                            name="description"
                            className="mb-0"
                            rules={[{ required: true, whitespace: true, message: "Please enter answer." }]}
                        >
                            <Input.TextArea
                                placeholder="Enter answer"
                                value={draft.description}
                                onChange={(e) => onChange("description", e.target.value)}
                                rows={4}
                                className="ai-kb-description-textarea"
                            />
                        </Form.Item>
                    )}

                    <div className="ai-kb-editor-actions">
                        <Button onClick={handleSave} disabled={isSaving || isUploading} size="default" type="primary" shape="circle" className="ai-kb-save-btn">
                            {isSaving ? "Loading..." : "Save"}
                        </Button>
                        <Button size="default" outlined shape="circle" className="ai-kb-cancel-btn" type="primary" onClick={onCancel} disabled={isSaving || isUploading}>
                            Cancel
                        </Button>
                    </div>
                </div>
            </Form>
        </div>
    );
};

// ─── Group Title Editor ────

export const GroupTitleEditor = ({ initialTitle = "", isSaving, onSave, onCancel }) => {
    const [title, setTitle] = useState(initialTitle);

    const handleSave = () => {
        if (!title.trim()) {
            Notification.addNotificationError("Please enter a group title.");
            return;
        }
        onSave(title.trim());
    };

    return (
        <div className="ai-kb-resource-card ai-kb-resource-card-editor">
            <div className="ai-kb-editor-fields w-100">
                <Input
                    placeholder="Enter group title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onPressEnter={handleSave}
                    className="h-50 ai-kb-question-input"
                    autoFocus
                />
                <div className="ai-kb-editor-actions">
                    <Button onClick={handleSave} disabled={isSaving} size="default" type="primary" shape="circle" className="ai-kb-save-btn">
                        {isSaving ? "Loading..." : "Save"}
                    </Button>
                    <Button size="default" outlined shape="circle" className="ai-kb-cancel-btn" type="primary" onClick={onCancel} disabled={isSaving}>
                        Cancel
                    </Button>
                </div>
            </div>
        </div>
    );
};

// ─── Group Header Row ────

export const GroupHeaderRow = ({ group, onEditGroup, onDeleteGroup, readOnly = false, showToggle = false }) => (
    <div className="ai-kb-group-header-row">
        <h4 className="project-tab-title">{group.title}</h4>
        <div className="ai-kb-resource-actions">
            {/* {showToggle && (
                <Switch size="large" className="switch-toggle" defaultChecked />
            )} */}
            {!readOnly && (
                <>
                    <button type="button" className="ai-kb-icon-btn" onClick={() => onEditGroup(group)} aria-label="Edit group">
                        <img src={editIcon} alt="edit" />
                    </button>
                    <button type="button" className="ai-kb-icon-btn is-delete" onClick={() => onDeleteGroup(group)} aria-label="Delete group">
                        <img src={deleteIcon} alt="delete" />
                    </button>
                </>
            )}
        </div>
    </div>
);

// ─── Listing Selection Table ───

export const ListingTable = ({ sortedListingLists, tempSelectedListings, onCheckboxChange, backEndPrefix }) => {
    const columns = [{ title: "Listing", dataIndex: "listing_name", key: "listing_name" }];

    const dataSource = sortedListingLists.map((item) => ({
        key: item.slug,
        listing_name: (
            <div className="user-info">
                <div className="user-info-checkbox">
                    <Form.Item className="mb-0" name="listing_slug" labelCol={{ className: "custom-checkbox-label" }}>
                        <Checkbox
                            onChange={(e) => onCheckboxChange(e, item.slug)}
                            checked={tempSelectedListings.includes(item.slug)}
                        />
                    </Form.Item>
                </div>
                <figure>
                    <img className="user-info-image" src={item?.listing_image} alt="" />
                </figure>
                <figcaption>
                    <Heading className="user-name" as="h6">
                        {global.checkPermission("listing-show") ? (
                            <Link target="_blank" className="boldurlLink" to={`${backEndPrefix}/listing/detail/${item.slug}`}>
                                {item?.listing_name}
                            </Link>
                        ) : (
                            <span>{item?.listing_name}</span>
                        )}
                    </Heading>
                    <span className="user-designation" />
                </figcaption>
            </div>
        ),
    }));

    return (
        <UserTableStyleWrapper>
            <div className="contact-table">
                <TableWrapper className="table-responsive">
                    <Table
                        dataSource={dataSource}
                        columns={columns}
                        rowKey="key"
                        pagination={false}
                        className="listing-no-thead-table"
                    />
                </TableWrapper>
            </div>
        </UserTableStyleWrapper>
    );
};
